// fw Sheet — a bottom sheet on phone, a centred dialog on desktop.
//
// Focus is trapped inside; Escape closes; the body scroll lock is the
// EXISTING counted lock in ui/hooks/useBodyScrollLock.js (one lock for the
// whole bundle — a second implementation is the bug that hook exists to end).
// Backdrop click closes; a click inside never does. Height uses 100dvh with a
// 100vh fallback (viewportUnits.test.js checks the pairing).
//
// ── THE SOFT KEYBOARD IS A FOURTH GEOMETRY BUG, AND dvh CANNOT SEE IT ──────
//
// Reported 2026-09-18: "iphone workers can't clock out". Clocking out opens
// StopSheet, whose confirm button lives in this component's FOOTER — outside
// `.fw-sheet__body`, so it does not scroll — inside a backdrop that is
// `position: fixed; inset: 0`.
//
// `position: fixed` and `dvh` are both measured against the LAYOUT viewport.
// client/public/index.html declares `interactive-widget=resizes-visual`, which
// is also the iOS default: opening the keyboard shrinks the VISUAL viewport
// and leaves the layout viewport alone. So the sheet keeps its full height,
// its bottom stays at the bottom of the layout viewport, and the keyboard is
// drawn on top of it. The button a crew member has to press to stop their
// clock is behind the keyboard, and the keyboard is open because the reason
// box is required and they just tapped it.
//
// viewportUnits.test.js's header lists three mechanisms behind the same
// complaint — "I have to turn the phone sideways before the buttons show up":
// OVERFLOW, OCCLUSION, and GEOMETRY against a viewport the worker cannot see.
// This is the fourth, and it is that third one again with a different hider:
// dvh is the height that is really visible *of the page*, and it does not
// subtract the keyboard.
//
// `window.visualViewport` is the only thing that knows. While the sheet is
// open we track it and pin the backdrop to the region that is actually
// visible, so the footer sits above the keyboard instead of behind it.
// Everything is inline on the element and reverted on close: the stylesheet
// keeps its exact `inset: 0` / `dvh` behaviour as the fallback for jsdom, old
// WebViews and anything without visualViewport.

import React, { useCallback, useEffect, useId, useRef } from 'react';
import { Icon } from '../../components/Icon.js';
import { lockBodyScroll } from '../../hooks/useBodyScrollLock.js';
import './Sheet.css';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function Sheet({ open, title, onClose, children, footer, closeOnBackdrop = true, className = '', ...rest }) {
  const titleId = useId();
  const panelRef = useRef(null);
  const backdropRef = useRef(null);
  const lastFocusedRef = useRef(null);

  // Body scroll lock, through the shared counted lock.
  useEffect(() => {
    if (!open) return undefined;
    return lockBodyScroll();
  }, [open]);

  // ── Keep the sheet inside the region the keyboard is not covering ───────
  //
  // Reads visualViewport and writes `top` / `height` on the backdrop, plus a
  // matching max-height on the panel so a tall sheet cannot spill back out
  // under the keyboard. With no keyboard these equal the layout viewport and
  // nothing moves; with no visualViewport at all nothing is written and the
  // stylesheet's `inset: 0` stands.
  useEffect(() => {
    if (!open) return undefined;
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return undefined;

    // Captured ONCE, here, and used by both `apply` and the cleanup. Reading
    // `.current` in a cleanup is the react-hooks/exhaustive-deps warning that
    // failed the zero-warning build, and it is a real one rather than a style
    // note: by the time cleanup runs the ref can point at a different node, so
    // the inline styles would be stripped from the wrong element and left on
    // this one. These nodes live exactly as long as this effect does.
    const backdrop = backdropRef.current;
    if (!backdrop) return undefined;

    const apply = () => {
      // offsetTop is how far the visual viewport has been PUSHED DOWN — which
      // iOS does when it scrolls a focused field into view. Without it the
      // sheet would be pinned to a region the page has scrolled away from.
      backdrop.style.top = `${vv.offsetTop}px`;
      backdrop.style.height = `${vv.height}px`;
      backdrop.style.bottom = 'auto';
      // Published as a VARIABLE, not forced as `max-height: 100%` on the
      // panel. 100% of the pinned backdrop removed the deliberate gap the
      // stylesheet leaves above a phone sheet (`calc(100dvh - space-6)`) — so
      // a tall sheet went edge to edge and `closeOnBackdrop` lost the strip of
      // backdrop that is its tap target. Raised in review. Sheet.css keeps
      // ownership of the gap and just measures against this instead of dvh.
      backdrop.style.setProperty('--fw-sheet-vh', `${vv.height}px`);
    };
    apply();

    // `scroll` as well as `resize`: iOS fires scroll on the visual viewport
    // when it shifts the page to reveal a focused input, with no resize.
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      // Put the stylesheet back in charge. The element is usually unmounting
      // anyway, but this component stays mounted across an `open` flip.
      backdrop.style.top = ''; backdrop.style.height = ''; backdrop.style.bottom = '';
      backdrop.style.removeProperty('--fw-sheet-vh');
    };
  }, [open]);

  // Escape closes, from anywhere on the page while the sheet is open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (onClose) onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Initial focus in, and back out on close.
  useEffect(() => {
    if (!open) return undefined;
    lastFocusedRef.current = typeof document !== 'undefined' ? document.activeElement : null;
    const panel = panelRef.current;
    if (panel) {
      const target = panel.querySelector('[data-fw-sheet-close]') || panel.querySelector(FOCUSABLE) || panel;
      if (target && target.focus) target.focus();
    }
    return () => {
      const el = lastFocusedRef.current;
      lastFocusedRef.current = null;
      if (el && el.focus) {
        try { el.focus(); } catch (_) { /* gone */ }
      }
    };
  }, [open]);

  const trapTab = useCallback((e) => {
    if (e.key !== 'Tab' || !panelRef.current) return;
    const focusable = Array.from(panelRef.current.querySelectorAll(FOCUSABLE));
    if (focusable.length === 0) { e.preventDefault(); return; }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }, []);

  if (!open) return null;

  const onBackdrop = (e) => {
    if (!closeOnBackdrop) return;
    if (e.target === e.currentTarget && onClose) onClose();
  };

  return (
    <div ref={backdropRef} className="fw-sheet-backdrop" onClick={onBackdrop} role="presentation">
      <div
        ref={panelRef}
        className={['fw-sheet', className].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        onKeyDown={trapTab}
        {...rest}
      >
        <div className="fw-sheet__handle" aria-hidden="true" />
        <header className="fw-sheet__head">
          {title ? <h2 id={titleId} className="fw-sheet__title">{title}</h2> : <span />}
          <button type="button" className="fw-sheet__close" onClick={onClose} aria-label="Close" data-fw-sheet-close>
            <Icon name="x" />
          </button>
        </header>
        <div className="fw-sheet__body">{children}</div>
        {footer ? <footer className="fw-sheet__footer">{footer}</footer> : null}
      </div>
    </div>
  );
}

export default Sheet;
